// src/app/pages/DataRequestFormPage.tsx
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { CheckCircle, AlertCircle, Loader2, Save, Download, Upload, FileText, Info, Camera } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { WzSearch } from '../components/WzSearch';
import { FoerderListe, type FoerderEintrag } from '../components/FoerderListe';
import { BafaCheckPopup } from '../components/BafaCheckPopup';
import { SteuerIdWarnModal } from '../components/SteuerIdWarnModal';
import { API_BASE } from '../lib/apiBase';
import { lookupPlz } from '../lib/plzLookup';
import { istVermutlichSteuernummerStattIdnr } from '../lib/steuerId';

type Status = 'loading' | 'ready' | 'submitting' | 'saving' | 'done' | 'dauer' | 'error' | 'used' | 'expired' | 'invalid';

const RECHTSFORMEN = [
  { group: 'Natürliche Person', options: ['Einzelunternehmen', 'Freiberufler/in', 'GbR'] },
  { group: 'Juristische Person (Kapitalgesellschaft)', options: ['GmbH', 'mbH', 'UG (haftungsbeschränkt)', 'AG', 'GmbH & Co. KG'] },
  { group: 'Juristische Person (Personengesellschaft)', options: ['OHG', 'KG', 'Partnerschaftsgesellschaft'] },
  { group: 'Sonstige', options: ['e.V.', 'eG', 'KdöR', 'Stiftung'] },
];

function rechtsformToPersonentyp(rechtsform: string): string {
  if (!rechtsform) return '';
  const natuerlich = RECHTSFORMEN[0].options;
  return natuerlich.includes(rechtsform) ? 'Natürliche Person' : 'Juristische Person';
}

function Tooltip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex group/tip ml-1">
      <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center cursor-help flex-shrink-0 text-white font-bold leading-none" style={{ fontSize: '11px' }}>i</span>
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-slate-800 text-white text-sm rounded-lg px-3 py-2 opacity-0 group-hover/tip:opacity-100 pointer-events-none transition-opacity z-50 shadow-lg normal-case font-normal whitespace-pre-line text-left">
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
      </span>
    </span>
  );
}

const inputClass = 'w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500';
const labelClass = 'flex items-center text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5';

/** Upload der unterschriebenen Dauerdokumente (eine oder mehrere Dateien, z.B. Scan + Fotos
 *  einzelner Seiten) — eigene Mini-Komponente, damit der eigene Lade-/Fehlerzustand nicht den
 *  großen Formular-Status (status/errorMsg) der Seite mitbenutzt. */
function DauerUploadBlock({ token, onUploaded }: { token: string; onUploaded: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await fetch(`${API_BASE}/api/customer-invitations/form/${token}/dauerdokumente`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || 'Hochladen fehlgeschlagen.');
      }
      onUploaded();
    } catch (err: any) {
      setError(err.message || 'Hochladen fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="flex flex-col items-center justify-center gap-2 w-full px-4 py-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-purple-400 transition-colors text-center">
        <Upload className="w-5 h-5 text-slate-400" />
        <span className="text-sm text-slate-600 dark:text-slate-400">
          {files.length ? `${files.length} Datei(en) ausgewählt` : 'Unterschriebene Unterlagen auswählen (PDF oder Foto)'}
        </span>
        <input
          type="file"
          accept="application/pdf,image/*"
          multiple
          className="hidden"
          onChange={e => setFiles(Array.from(e.target.files ?? []))}
        />
      </label>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      <button
        onClick={handleUpload}
        disabled={!files.length || uploading}
        className="mt-3 w-full px-4 py-2.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
        Unterschriebene Unterlagen hochladen
      </button>
    </div>
  );
}

export default function DataRequestFormPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [consultantName, setConsultantName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [dsgvoConsent, setDsgvoConsent] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const showErrorsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoad = useRef(true);

  // System dark mode detection
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const [form, setForm] = useState({
    name: '', antragstellerTyp: '', rechtsform: '', gruendung: '',
    anrede: '', vorname: '', nachname: '',
    address: '', hausnummer: '', plz: '', ort: '', land: 'Deutschland',
    email: '', phoneVorwahl: '', phoneNummer: '', website: '', iban: '',
    personentyp: '', artJuristischePerson: '',
    steueridentnummer: '', steuernummer: '', taxId: '', geburtsdatum: '',
    wirtschaftszweig: '', mitarbeiterzahl: '', jahresumsatz: '',
    jahresbilanzsumme: '', vorsteuerabzug: '', unternehmenstyp: 'Eigenständiges Unternehmen',
    geschaeftsgegenstand: '', notes: '',
    foerderungen: [] as FoerderEintrag[],
  });
  const [checkPopup, setCheckPopup] = useState<{ open: boolean; ampel?: string | null; reasons?: { code: string; severity: string }[] }>({ open: false });
  const [steuerIdWarnOpen, setSteuerIdWarnOpen] = useState(false);
  // Nicht bei jedem Blur erneut nerven, solange sich der (falsche) Wert nicht ändert.
  const steuerIdWarnedValueRef = useRef<string | null>(null);
  // Dauerdokumente (Vollmacht/GRC/De-Minimis) direkt im Formular, Kundenwunsch 14.10.2026:
  // kein separater Signatur-Link/-Mail — die Dokumente stehen erst nach dem Absenden zur
  // Verfügung (brauchen die gerade eingegebenen Kundendaten), siehe submit()-Rückgabewert.
  const [vollmachtUploaded, setVollmachtUploaded] = useState(false);
  // Nur bei angeforderten Dauerdokumenten gibt es überhaupt mehr als den einen Formular-Schritt.
  const [includeDauerdokumente, setIncludeDauerdokumente] = useState(false);
  // Einzelbildschirme auf der Dauerdokumente-Seite, analog zum desktopStep in SigningPage.tsx.
  const [dauerStep, setDauerStep] = useState<'herunterladen' | 'unterschreiben' | 'hochladen'>('herunterladen');
  const [handConfirmed, setHandConfirmed] = useState(false);
  // Hochladen-Schritt: erst die Wahl (wie bei SigningPage.tsx), dann entweder PDF-Dropzone
  // auf diesem Gerät oder QR-Code zum Fortsetzen auf dem Handy (eigene Kamera).
  const [uploadChoice, setUploadChoice] = useState<'ask' | 'pdf' | 'qr'>('ask');
  const [qrCodeUrl] = useState(() => (typeof window === 'undefined' ? '' : window.location.href));

  // Solange der QR-Code steht, alle paar Sekunden nachfragen, ob sich vom Handy aus am
  // Upload-Stand etwas getan hat — das Handy meldet sich nicht von sich aus (wie SigningPage.tsx).
  useEffect(() => {
    if (uploadChoice !== 'qr' || !token) return;
    const iv = setInterval(() => {
      fetch(`${API_BASE}/api/customer-invitations/form/${token}`)
        .then(r => (r.ok ? r.json() : null))
        .then(fresh => {
          if (fresh?.vollmachtUploaded) {
            clearInterval(iv);
            setVollmachtUploaded(true);
          }
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(iv);
  }, [uploadChoice, token]);

  useEffect(() => {
    fetch(`${API_BASE}/api/customer-invitations/form/${token}`)
      .then(r => {
        if (r.status === 410) return r.json().then(d => { const e = new Error(); (e as any).type = d.message?.includes('bereits') ? 'used' : 'expired'; throw e; });
        if (!r.ok) { const e = new Error(); (e as any).type = 'invalid'; throw e; }
        return r.json();
      })
      .then(data => {
        setConsultantName(data.consultantName);
        if (data.existingData) {
          setForm(prev => ({ ...prev, ...data.existingData }));
        }
        setVollmachtUploaded(!!data.vollmachtUploaded);
        setIncludeDauerdokumente(!!data.includeDauerdokumente);
        // Link mit bereits hochgeladenen Unterlagen erneut geöffnet → direkt den
        // Hochladen-Schritt zeigen (dort steht die "vielen Dank"-Bestätigung), nicht wieder bei 1 starten.
        if (data.vollmachtUploaded) setDauerStep('hochladen');
        // Link ein zweites Mal geöffnet (Formular schon abgeschickt, Dauerdokumente schon
        // generiert) → direkt zum Download/Upload-Schritt, nicht wieder das ganze Formular.
        setStatus(data.includeDauerdokumente && data.dauerdokumenteReady ? 'dauer' : 'ready');
      })
      .catch(err => setStatus(err?.type || 'invalid'));
  }, [token]);

  // Schrittanzeige wie beim Unterschriften-Upload (SigningPage): Kreise mit Verbindungslinien,
  // aktiv/erledigt/offen. "Daten" steht für die Formularseite (status 'ready'); Herunterladen/
  // Unterschreiben/Hochladen sind die drei Einzelbildschirme der Dauerdokumente-Seite (dauerStep).
  const dauerStepOrder = ['daten', 'herunterladen', 'unterschreiben', 'hochladen'] as const;
  const dauerStepLabels: Record<typeof dauerStepOrder[number], string> = {
    daten: 'Daten', herunterladen: 'Herunterladen', unterschreiben: 'Unterschreiben', hochladen: 'Hochladen',
  };
  const dauerCurrentIdx = vollmachtUploaded
    ? dauerStepOrder.length
    : status === 'dauer' ? dauerStepOrder.indexOf(dauerStep) : 0;
  const renderDauerStepBar = () => includeDauerdokumente && (
    <div className="flex items-center justify-center gap-2 mb-6">
      {dauerStepOrder.map((s, i) => {
        const active = i === dauerCurrentIdx;
        const done = i < dauerCurrentIdx;
        return (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 text-xs font-medium ${active ? 'text-purple-600 dark:text-purple-400' : done ? 'text-slate-500 dark:text-slate-400' : 'text-slate-300 dark:text-slate-600'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${active ? 'bg-purple-600 text-white' : done ? 'bg-slate-400 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'}`}>
                {done ? '✓' : i + 1}
              </span>
              {dauerStepLabels[s]}
            </div>
            {i < dauerStepOrder.length - 1 && <div className={`w-6 h-px ${done ? 'bg-slate-400' : 'bg-slate-200 dark:bg-slate-700'}`} />}
          </div>
        );
      })}
    </div>
  );

  const requiredFilled =
    form.name && form.antragstellerTyp && form.rechtsform && form.gruendung && form.anrede &&
    form.vorname && form.nachname && form.address && form.hausnummer && form.plz &&
    form.ort && form.email && form.phoneVorwahl && form.phoneNummer && form.iban && form.wirtschaftszweig &&
    form.geschaeftsgegenstand &&
    form.personentyp && form.vorsteuerabzug && form.mitarbeiterzahl &&
    (form.jahresumsatz || form.jahresbilanzsumme) &&
    form.unternehmenstyp &&
    (form.personentyp === 'Natürliche Person' ? (form.steueridentnummer && form.geburtsdatum) : true) &&
    (form.personentyp === 'Juristische Person' ? (form.artJuristischePerson && form.steuernummer) : true);

  const canSubmit = requiredFilled && dsgvoConsent;

  const triggerShowErrors = () => {
    setShowErrors(true);
    if (showErrorsTimerRef.current) clearTimeout(showErrorsTimerRef.current);
    showErrorsTimerRef.current = setTimeout(() => setShowErrors(false), 12000);
    // scroll to first error after React re-renders
    requestAnimationFrame(() => {
      const el = document.querySelector('.border-red-400');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const handleSubmit = async () => {
    triggerShowErrors();
    if (!canSubmit) return;
    setStatus('submitting');
    try {
      const res = await fetch(`${API_BASE}/api/customer-invitations/form/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, dsgvoConsent: true }),
      });
      if (res.status === 410) {
        const d = await res.json();
        setStatus(d.message?.includes('bereits') ? 'used' : 'expired');
        return;
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setErrorMsg(d.message || 'Fehler beim Absenden.');
        setStatus('ready');
        return;
      }
      const result = await res.json().catch(() => null);
      if (result) setCheckPopup({ open: true, ampel: result.ampel, reasons: result.reasons ?? [] });
      setStatus(result?.dauerdokumenteReady ? 'dauer' : 'done');
    } catch {
      setErrorMsg('Verbindungsfehler. Bitte versuchen Sie es erneut.');
      setStatus('ready');
    }
  };

  const handleSave = async () => {
    if (status === 'submitting' || status === 'saving') return;
    setStatus('saving');
    setSaveSuccess(false);
    try {
      const res = await fetch(`${API_BASE}/api/customer-invitations/form/${token}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch {}
    setStatus('ready');
  };

  // Auto-save: 3s after last change, skip initial data load
  useEffect(() => {
    if (isInitialLoad.current) { isInitialLoad.current = false; return; }
    if (status === 'submitting' || status === 'done') return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      fetch(`${API_BASE}/api/customer-invitations/form/${token}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      }).then(r => {
        if (r.ok) { setSaveSuccess(true); setTimeout(() => setSaveSuccess(false), 2000); }
      }).catch(() => {});
    }, 2000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [form]);

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  // PLZ → Ort/Land: bei 5-stelliger PLZ Orte als Vorschläge fürs Ort-Feld laden,
  // Land mit „Deutschland" vorbelegen (bleibt editierbar).
  const [ortSuggestions, setOrtSuggestions] = useState<string[]>([]);
  const handlePlzChange = (value: string) => {
    setForm(prev => ({ ...prev, plz: value }));
    if (!/^\d{5}$/.test(value)) { setOrtSuggestions([]); return; }
    lookupPlz(value).then(r => {
      if (!r) { setOrtSuggestions([]); return; }
      setOrtSuggestions(r.orte);
      setForm(prev => ({
        ...prev,
        land: prev.land?.trim() ? prev.land : r.land,
        ort: r.orte.length === 1 ? r.orte[0] : prev.ort,
      }));
    });
  };

  const err = (isEmpty: boolean) =>
    showErrors && isEmpty ? ' !border-red-400 bg-red-50 dark:!border-red-500 dark:bg-red-900/20' : '';

  if (status === 'loading') return (
    <div className={systemDark ? 'dark' : ''}>
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    </div>
  );

  if (status === 'done') return (
    <>
      <div className={systemDark ? 'dark' : ''}>
        <div className="min-h-screen flex items-center justify-center p-4 bg-white dark:bg-slate-900">
          <div className="text-center max-w-md">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Vielen Dank!</h1>
            <p className="text-slate-600 dark:text-slate-400">Ihre Daten wurden erfolgreich übermittelt. Sie können dieses Fenster schließen.</p>
            <button
              onClick={() => window.close()}
              className="mt-6 px-5 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors cursor-pointer"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
      <BafaCheckPopup open={checkPopup.open} ampel={checkPopup.ampel} reasons={checkPopup.reasons}
        onClose={() => setCheckPopup({ open: false })} />
    </>
  );

  if (status === 'dauer') return (
    <>
      <div className={systemDark ? 'dark' : ''}>
        <div className="min-h-screen bg-slate-100 dark:bg-slate-900 py-12 px-4">
          <div className="max-w-xl mx-auto">
            {/* Header — wie SigningPage.tsx: Icon-Kreis + zentrierte Überschrift/Untertitel,
                außerhalb der Schritt-Karten. */}
            <div className="text-center mb-8">
              <div className="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-purple-600 dark:text-purple-400" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Vollmacht unterschreiben &amp; hochladen</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-2">
                Zum Abschluss benötigen wir noch Ihre unterschriebene Vollmacht samt der weiteren Erklärungen.
              </p>
            </div>

            {renderDauerStepBar()}

            {dauerStep === 'herunterladen' && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-4">
                <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Schritt 1: Unterlagen herunterladen</h2>
                <div className="flex flex-col gap-3">
                  <a
                    href={`${API_BASE}/api/customer-invitations/form/${token}/dauerdokumente/all`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 px-4 py-3 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Download className="w-5 h-5" />
                    <div className="text-left">
                      <div>Alle Dokumente herunterladen</div>
                      <div className="text-slate-400 dark:text-slate-400 text-xs font-normal">PDF mit Vollmacht, GRC- und De-Minimis-Erklärung</div>
                    </div>
                  </a>
                  <a
                    href={`${API_BASE}/api/customer-invitations/form/${token}/dauerdokumente/signature-pages`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    <Download className="w-5 h-5" />
                    <div className="text-left">
                      <div>Nur Unterschriftseiten</div>
                      <div className="text-purple-200 text-xs font-normal">PDF nur mit den Seiten, die unterschrieben werden müssen</div>
                    </div>
                  </a>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setDauerStep('unterschreiben')}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    Weiter
                  </button>
                </div>
              </div>
            )}

            {dauerStep === 'unterschreiben' && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-4">
                <button
                  onClick={() => setDauerStep('herunterladen')}
                  className="text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 mb-4"
                >
                  ← Zurück
                </button>
                <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Schritt 2: Unterlagen unterschreiben</h2>
                <ol className="space-y-3 mb-5">
                  <li className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center text-sm font-semibold flex-shrink-0">1</span>
                    <span className="text-slate-700 dark:text-slate-300">Unterlagen ausdrucken</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center text-sm font-semibold flex-shrink-0">2</span>
                    <span className="text-slate-900 dark:text-white font-semibold">Mit Stift auf Papier unterschreiben</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center text-sm font-semibold flex-shrink-0">3</span>
                    <span className="text-slate-700 dark:text-slate-300">Fotografieren oder einscannen</span>
                  </li>
                </ol>
                <div className="rounded-lg bg-slate-50 dark:bg-slate-700/40 border border-slate-100 dark:border-slate-700 p-4 flex items-start gap-3 mb-5">
                  <Info className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">Bitte handschriftlich unterschreiben</p>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">Eine digitale Signatur wird für diesen Vorgang nicht akzeptiert.</p>
                  </div>
                </div>
                <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={handConfirmed}
                      onChange={e => setHandConfirmed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 accent-purple-600"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Ich habe die Unterlagen mit Stift auf Papier unterschrieben.
                    </span>
                  </label>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => setDauerStep('hochladen')}
                      disabled={!handConfirmed}
                      className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-semibold"
                    >
                      Weiter
                    </button>
                  </div>
                </div>
              </div>
            )}

            {dauerStep === 'hochladen' && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-4">
                {!vollmachtUploaded && (
                  <button
                    onClick={() => setDauerStep('unterschreiben')}
                    className="text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 mb-4"
                  >
                    ← Zurück
                  </button>
                )}
                <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Schritt 3: Unterschriebene Unterlagen hochladen</h2>
                {vollmachtUploaded ? (
                  <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                    <CheckCircle className="w-6 h-6 flex-shrink-0" />
                    <span className="font-medium">Vielen Dank! Unterlagen erfolgreich übermittelt.</span>
                  </div>
                ) : uploadChoice === 'ask' ? (
                  <div className="flex flex-col items-center gap-4 text-center py-2">
                    <p className="text-base font-medium text-slate-800 dark:text-slate-200">Wie möchten Sie die unterschriebenen Unterlagen hochladen?</p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setUploadChoice('qr')}
                        className="flex items-center gap-2 px-5 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        <Camera className="w-4 h-4" /> Kamera (Handy)
                      </button>
                      <button
                        onClick={() => setUploadChoice('pdf')}
                        className="flex items-center gap-2 px-5 py-3 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg transition-colors text-sm font-medium"
                      >
                        <Upload className="w-4 h-4" /> PDF hochladen
                      </button>
                    </div>
                  </div>
                ) : uploadChoice === 'qr' ? (
                  <div className="text-center">
                    <div className="bg-white p-3 rounded-lg border border-slate-200 mx-auto w-fit">
                      <QRCodeSVG value={qrCodeUrl} size={176} />
                    </div>
                    <p className="text-base font-medium text-slate-800 dark:text-slate-200 mt-4">
                      QR-Code mit der Handy-Kamera scannen und ein <strong>Foto der unterschriebenen Unterlagen</strong> hochladen.
                    </p>
                    <button
                      onClick={() => setUploadChoice('ask')}
                      className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 underline mt-3"
                    >
                      ← Zurück zur Auswahl
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setUploadChoice('ask')}
                      className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 underline mb-3"
                    >
                      ← Zurück zur Auswahl
                    </button>
                    <DauerUploadBlock token={token!} onUploaded={() => setVollmachtUploaded(true)} />
                  </>
                )}
              </div>
            )}

            <button
              onClick={() => window.close()}
              className="w-full px-4 py-2.5 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors cursor-pointer"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
      <BafaCheckPopup open={checkPopup.open} ampel={checkPopup.ampel} reasons={checkPopup.reasons}
        onClose={() => setCheckPopup({ open: false })} />
    </>
  );

  const errorMessages: Record<string, string> = {
    invalid: 'Dieser Link ist ungültig.',
    used: 'Dieser Link wurde bereits verwendet.',
    expired: 'Dieser Link ist abgelaufen (gültig für 14 Tage).',
  };

  if (['invalid', 'used', 'expired'].includes(status)) return (
    <div className={systemDark ? 'dark' : ''}>
      <div className="min-h-screen flex items-center justify-center p-4 bg-white dark:bg-slate-900">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Link nicht gültig</h1>
          <p className="text-slate-600 dark:text-slate-400">{errorMessages[status]}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={systemDark ? 'dark' : ''}>
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {renderDauerStepBar()}
          {/* Header */}
          <div className="bg-purple-600 rounded-xl p-6 mb-6 text-white">
            <h1 className="text-xl font-bold mb-1">Ihre Unternehmensdaten</h1>
            <p className="text-purple-100 text-sm">
              Bitte füllen Sie die folgenden Felder aus, damit Ihr Beratungsantrag vollständig eingereicht werden kann.
              Mit * gekennzeichnete Felder sind Pflichtfelder.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 space-y-4">

            {/* Stammdaten */}
            <section className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Angaben zur antragstellenden Person</h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Bei der antragstellenden Person handelt es sich um *</label>
                  <div className={`flex flex-wrap gap-3 mt-1 rounded-lg p-2 -m-2${err(!form.antragstellerTyp)}`}>
                    {['Freiberuflich tätige Person', 'Unternehmen'].map(opt => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="antragstellerTyp"
                          checked={form.antragstellerTyp === opt}
                          onChange={() => setForm(prev => ({ ...prev, antragstellerTyp: opt }))}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 items-end">
                  <div className="w-full sm:w-2/3">
                    <label className={labelClass}>Firmenname *</label>
                    <input type="text" value={form.name} onChange={set('name')} className={`${inputClass} h-[38px]${err(!form.name)}`} />
                  </div>
                  <div className="w-full sm:flex-1">
                    <label className={labelClass}>Unternehmensform *</label>
                    <select value={form.rechtsform} onChange={e => setForm(prev => ({ ...prev, rechtsform: e.target.value, personentyp: rechtsformToPersonentyp(e.target.value) }))} className={`${inputClass} h-[38px]${err(!form.rechtsform)}`}>
                      <option value="">Bitte wählen</option>
                      {RECHTSFORMEN.map(g => (
                        <optgroup key={g.group} label={g.group}>
                          {g.options.map(o => <option key={o} value={o}>{o}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </section>

            {/* Ansprechperson */}
            <section className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Ansprechperson</h2>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-28 sm:flex-shrink-0">
                  <label className={labelClass}>Anrede *</label>
                  <select value={form.anrede} onChange={set('anrede')} className={`${inputClass} h-[38px]${err(!form.anrede)}`}>
                    <option value="">Bitte wählen</option>
                    <option value="Herr">Herr</option>
                    <option value="Frau">Frau</option>
                    <option value="Divers">Divers</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className={labelClass}>Vorname *</label>
                  <input type="text" value={form.vorname} onChange={set('vorname')} className={`${inputClass}${err(!form.vorname)}`} />
                </div>
                <div className="flex-1">
                  <label className={labelClass}>Nachname *</label>
                  <input type="text" value={form.nachname} onChange={set('nachname')} className={`${inputClass}${err(!form.nachname)}`} />
                </div>
              </div>
            </section>

            {/* Adresse */}
            <section className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Anschrift</h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className={labelClass}>Straße *</label>
                    <input type="text" value={form.address} onChange={set('address')} className={`${inputClass}${err(!form.address)}`} />
                  </div>
                  <div className="w-20 flex-shrink-0">
                    <label className={labelClass}>Nr. *</label>
                    <input type="text" value={form.hausnummer} onChange={set('hausnummer')} className={`${inputClass}${err(!form.hausnummer)}`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <label className={labelClass}>PLZ *</label>
                    <input type="text" inputMode="numeric" value={form.plz} onChange={e => handlePlzChange(e.target.value)} className={`${inputClass}${err(!form.plz)}`} />
                  </div>
                  <div className="col-span-1 sm:col-span-2">
                    <label className={labelClass}>Ort *</label>
                    <input type="text" list="drf-plz-orte" value={form.ort} onChange={set('ort')} className={`${inputClass}${err(!form.ort)}`} />
                    {ortSuggestions.length > 0 && (
                      <datalist id="drf-plz-orte">
                        {ortSuggestions.map(o => <option key={o} value={o} />)}
                      </datalist>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className={labelClass}>Land</label>
                    <input type="text" value={form.land} onChange={set('land')} className={inputClass} />
                  </div>
                </div>
              </div>
            </section>

            {/* Kontaktdaten */}
            <section className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Kontakt &amp; Bankverbindung</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>E-Mail *</label>
                  <input type="email" value={form.email} onChange={set('email')} className={`${inputClass}${err(!form.email)}`} />
                </div>
                <div className="flex gap-2">
                  <div className="w-24 flex-shrink-0">
                    <label className={labelClass}>Vorwahl *</label>
                    <input type="tel" value={form.phoneVorwahl} onChange={set('phoneVorwahl')} className={`${inputClass}${err(!form.phoneVorwahl)}`} />
                  </div>
                  <div className="flex-1">
                    <label className={labelClass}>Rufnummer *</label>
                    <input type="tel" value={form.phoneNummer} onChange={set('phoneNummer')} className={`${inputClass}${err(!form.phoneNummer)}`} />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>IBAN *</label>
                  <input type="text" value={form.iban} onChange={set('iban')} className={`${inputClass}${err(!form.iban)}`} />
                </div>
                <div>
                  <label className={labelClass}>Website</label>
                  <input type="url" value={form.website} onChange={set('website')} className={inputClass} />
                </div>
              </div>
            </section>

            {/* Unternehmensdaten */}
            <section className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Unternehmensdaten</h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    Rechtsform *
                    <Tooltip text="Einzelunternehmen sind meist natürliche Personen. GmbH, UG, AG oder Vereine sind juristische Personen." />
                  </label>
                  <div className={`flex flex-wrap gap-3 mt-1 rounded-lg p-2 -m-2${err(!form.personentyp)}`}>
                    {['Natürliche Person', 'Juristische Person'].map(opt => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="personentyp"
                          checked={form.personentyp === opt}
                          onChange={() => setForm(prev => ({ ...prev, personentyp: opt }))}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    Vorsteuerabzugsberechtigt *
                    <Tooltip text={'Ja, wenn Ihr Unternehmen Vorsteuer geltend machen kann. Kleinunternehmer wählen meist „Nein". Wählen Sie „Teilweise", wenn es nur für bestimmte Leistungen gilt.'} />
                  </label>
                  <div className={`flex flex-wrap gap-3 mt-1 rounded-lg p-2 -m-2${err(!form.vorsteuerabzug)}`}>
                    {['Ja', 'Nein', 'Teilweise'].map(opt => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="vorsteuerabzug"
                          checked={form.vorsteuerabzug === opt}
                          onChange={() => setForm(prev => ({ ...prev, vorsteuerabzug: opt }))}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {form.personentyp === 'Natürliche Person' && (
                  <>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <label className={labelClass}>
                          Steuer-Identifikationsnummer *
                          <Tooltip text={`Prüfen Sie bitte, ob Sie wirklich die IdNr. / Identifikationsnummer genommen haben, nicht eine dieser Nummern:
• Steuernummer vom Finanzamt, oft im Format wie 12/345/67890
• eTIN auf älteren Lohnsteuerbescheinigungen
• Umsatzsteuer-ID
• BAFA-Vorgangsnummer oder Kundennummer

Sie finden die richtige Nummer meist auf dem Einkommensteuerbescheid, der Lohnsteuerbescheinigung oder im Schreiben vom Bundeszentralamt für Steuern. Sie ist 11-stellig und wird oft als „Identifikationsnummer", „steuerliche Identifikationsnummer" oder „IdNr." bezeichnet.`} />
                        </label>
                        <input type="text" value={form.steueridentnummer} onChange={set('steueridentnummer')} className={`${inputClass}${err(!form.steueridentnummer)}`}
                          onBlur={() => {
                            if (form.rechtsform === 'Einzelunternehmen' && istVermutlichSteuernummerStattIdnr(form.steueridentnummer)
                              && steuerIdWarnedValueRef.current !== form.steueridentnummer) {
                              steuerIdWarnedValueRef.current = form.steueridentnummer;
                              setSteuerIdWarnOpen(true);
                            }
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <label className={labelClass}>
                          USt-IdNr. (VAT-ID)
                          <Tooltip text={'Falls vorhanden. Deutsche USt-IdNrn. beginnen mit „DE".'} />
                        </label>
                        <input type="text" value={form.taxId} onChange={set('taxId')} className={inputClass} />
                      </div>
                    </div>
                    <div className="sm:w-1/2">
                      <label className={labelClass}>Geburtsdatum *</label>
                      <input type="date" value={form.geburtsdatum} onChange={set('geburtsdatum')} className={`${inputClass}${err(!form.geburtsdatum)}`} />
                    </div>
                  </>
                )}
                {form.personentyp === 'Juristische Person' && (
                  <>
                    <div>
                      <label className={labelClass}>
                        Art der juristischen Person *
                        <Tooltip text="GmbH, UG, AG und Vereine sind meist privatrechtlich. Öffentlich-rechtlich betrifft öffentliche Einrichtungen." />
                      </label>
                      <div className={`flex flex-wrap gap-3 mt-1 rounded-lg p-2 -m-2${err(!form.artJuristischePerson)}`}>
                        {['Privatrechtlich', 'Öffentlich-rechtlich'].map(opt => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="artJuristischePerson"
                              checked={form.artJuristischePerson === opt}
                              onChange={() => setForm(prev => ({ ...prev, artJuristischePerson: opt }))}
                              className="w-4 h-4 cursor-pointer"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <label className={labelClass}>
                          Steuernummer *
                          <Tooltip text="Die Steuernummer finden Sie auf Schreiben des Finanzamts oder Steuerbescheiden." />
                        </label>
                        <input type="text" value={form.steuernummer} onChange={set('steuernummer')} className={`${inputClass}${err(!form.steuernummer)}`} />
                      </div>
                      <div className="flex-1">
                        <label className={labelClass}>
                          USt-IdNr. (VAT-ID)
                          <Tooltip text={'Falls vorhanden. Deutsche USt-IdNrn. beginnen mit „DE".'} />
                        </label>
                        <input type="text" value={form.taxId} onChange={set('taxId')} className={inputClass} />
                      </div>
                    </div>
                  </>
                )}
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <div className="w-full sm:w-36 sm:flex-shrink-0">
                    <label className={labelClass}>
                      Gründungsdatum *
                      <Tooltip text="Datum der Gewerbeanmeldung oder Eintragung." />
                    </label>
                    <input type="text" value={form.gruendung} onChange={set('gruendung')} className={`${inputClass}${err(!form.gruendung)}`} />
                  </div>
                  <div className="w-full sm:flex-1">
                    <label className={labelClass}>
                      Wirtschaftszweig (WZ) *
                      <Tooltip text="Suchen Sie nach Ihrer Haupttätigkeit, z. B. Gastronomie, Friseur, Beratung, Einzelhandel. Wählen Sie den passendsten Eintrag aus der Liste." />
                    </label>
                    <WzSearch
                      value={form.wirtschaftszweig}
                      onChange={(val: string) => setForm(prev => ({ ...prev, wirtschaftszweig: val }))}
                      darkMode={systemDark}
                      error={showErrors && !form.wirtschaftszweig}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    Angemeldeter Geschäftsgegenstand *
                    <Tooltip text="Tragen Sie ein, welche Tätigkeit bei Gewerbeamt, Finanzamt oder Handelsregister angemeldet ist." />
                  </label>
                  <textarea
                    value={form.geschaeftsgegenstand}
                    onChange={set('geschaeftsgegenstand')}
                    onInput={e => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }}
                    className={inputClass + ' resize-none overflow-hidden' + err(!form.geschaeftsgegenstand)}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>
                      Anzahl Beschäftigte *
                      <Tooltip text="Durchschnittliche Anzahl zum Zeitpunkt der Antragstellung (inklusive Teilzeit, anteilig)." />
                    </label>
                    <input type="text" value={form.mitarbeiterzahl} onChange={set('mitarbeiterzahl')} className={`${inputClass}${err(!form.mitarbeiterzahl)}`} />
                  </div>
                  <div className={form.jahresbilanzsumme ? 'opacity-40' : ''}>
                    <label className={labelClass}>
                      Jahresumsatz (€) *
                      <Tooltip text="Umsatz des letzten abgeschlossenen Geschäftsjahres. Schätzung möglich." />
                    </label>
                    <input type="text" value={form.jahresumsatz} onChange={set('jahresumsatz')} disabled={!!form.jahresbilanzsumme} className={`${inputClass}${form.jahresbilanzsumme ? ' cursor-not-allowed' : ''}${err(!(form.jahresumsatz || form.jahresbilanzsumme))}`} />
                  </div>
                  <div className={form.jahresumsatz ? 'opacity-40' : ''}>
                    <label className={labelClass}>
                      Jahresbilanzsumme (€) *
                      <Tooltip text="Bilanzsumme des letzten abgeschlossenen Geschäftsjahres. Schätzung möglich." />
                    </label>
                    <input type="text" value={form.jahresbilanzsumme} onChange={set('jahresbilanzsumme')} disabled={!!form.jahresumsatz} className={`${inputClass}${form.jahresumsatz ? ' cursor-not-allowed' : ''}${err(!(form.jahresumsatz || form.jahresbilanzsumme))}`} />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    Unternehmenstyp *
                    <Tooltip text={'Wählen Sie „Eigenständig", wenn keine relevanten Unternehmensbeteiligungen bestehen. Bei Beteiligungen ab 25 % bitte „Partnerunternehmen" oder „Verbundenes Unternehmen" prüfen.'} />
                  </label>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {['Eigenständiges Unternehmen', 'Partnerunternehmen', 'Verbundenes Unternehmen'].map(opt => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="unternehmenstyp"
                          checked={form.unternehmenstyp === opt}
                          onChange={() => setForm(prev => ({ ...prev, unternehmenstyp: opt }))}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* De-minimis / Förderungen */}
            <section className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-2">De-minimis-Förderungen</h2>
              <FoerderListe
                eintraege={(form.foerderungen as FoerderEintrag[]) ?? []}
                grenze={300000}
                darkMode={systemDark}
                onChange={(list) => setForm(prev => ({ ...prev, foerderungen: list }))}
              />
            </section>

            {/* DSGVO + Absenden */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
              {errorMsg && (
                <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-400">
                  {errorMsg}
                </div>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dsgvoConsent}
                  onChange={e => setDsgvoConsent(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-purple-600 rounded border-slate-300 focus:ring-purple-500 cursor-pointer"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Ich stimme zu, dass meine Angaben zur Vorbereitung und Bearbeitung des BAFA-Beratungsantrags verarbeitet werden. *
                </span>
              </label>
              {showErrors && !requiredFilled && (
                <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-400">
                  Bitte füllen Sie alle Pflichtfelder aus.
                </div>
              )}
              {showErrors && requiredFilled && !dsgvoConsent && (
                <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-400">
                  Bitte stimmen Sie der Datenverarbeitung zu.
                </div>
              )}
              {saveSuccess && (
                <div className="px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  Zwischenstand gespeichert.
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={status === 'submitting' || status === 'saving'}
                  className="flex items-center gap-2 px-4 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {status === 'saving' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Zwischenspeichern
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={status === 'submitting' || status === 'saving'}
                  className="flex-1 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'submitting' && <Loader2 className="w-4 h-4 animate-spin" />}
                  Daten absenden
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BafaCheckPopup open={checkPopup.open} ampel={checkPopup.ampel} reasons={checkPopup.reasons}
        onClose={() => setCheckPopup({ open: false })} />
      <SteuerIdWarnModal open={steuerIdWarnOpen} onClose={() => setSteuerIdWarnOpen(false)} />
    </div>
  );
}
